import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface TeamMember {
    name: string;
    role: string;
    img: string;
    featured?: boolean;
    hospitalLink?: boolean;
    linkedin?: string;
    twitter?: string;
}

const teamMembers: TeamMember[] = [
    {
        name: 'David I. Mgbeke',
        role: 'Founder and Manager',
        img: '/image/david.jpeg',
        featured: true,
        hospitalLink: true,
    },
    {
        name: 'Dr Emmanuel Odinaka',
        role: 'Founder & CEO',
        img: '/image/dremma.jpeg',
        featured: true,
    },
    {
        name: 'Dr Fortunatus Obi',
        role: 'COO',
        img: '/image/fortunatus.jpeg',
        featured: true,
    },
    {
        name: 'Ikechukwu Lawrence',
        role: 'Director of Business and Logistics',
        img: '/image/lawrence.png',
    },
    {
        name: 'Arch. Samuel Daberechi',
        role: 'Director of Works and Maintenance',
        img: '/image/daberechi.png',
    },
    {
        name: 'Onyinyechi Patience',
        role: 'Director of Finance & Secretary',
        img: '/image/onyinyechi.jpeg',
    },
    {
        name: 'ThankGod Maduka',
        role: 'Head of IT',
        img: '/image/tg.jpg',
    },
    {
        name: 'Offor Chuks',
        role: 'Customer Service Manager',
        img: '/image/offor.png',
    },
    {
        name: 'Ifeanyi Daniel',
        role: 'Head of Laboratories',
        img: '/image/Ifeanyi.jpeg',
    },
];

const divisions = [
    {
        name: 'Leadership',
        color: 'blue' as const,
        icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
        ),
        roles: [
            { title: 'Chairman/Founder', desc: 'Oversees overall vision, strategy, and expansion.' },
            { title: 'CEO', desc: 'Manages daily operations, business growth, and partnerships.' },
            { title: 'COO', desc: 'Ensures the smooth delivery of healthcare services, logistics, and operational efficiency.' },
            { title: 'CFO', desc: 'Handles budgeting, financial planning, and revenue management.' },
        ],
    },
    {
        name: 'Healthcare',
        color: 'green' as const,
        icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        roles: [
            { title: 'Medical Director', desc: 'Supervises all clinical activities and ensures regulatory compliance.' },
            { title: 'Hospital/Clinic Manager', desc: 'Oversees hospital operations, staffing, and patient experience.' },
            { title: 'Chief Nursing Officer (CNO)', desc: 'Manages nursing staff and patient care quality.' },
            { title: 'Pharmacy Manager', desc: 'Oversees pharmaceutical procurement and distribution.' },
            { title: 'Medical Laboratory Director', desc: 'Supervises diagnostic testing and lab operations.' },
            { title: 'Telemedicine Coordinator', desc: 'Manages online consultations and digital healthcare services.' },
            { title: 'Home Care & Geriatric Services Manager', desc: 'Oversees home-based patient care.' },
        ],
    },
    {
        name: 'Business',
        color: 'blue' as const,
        icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M16 3v4M8 3v4" /></svg>
        ),
        roles: [
            { title: 'Business Development Manager', desc: 'Identifies growth opportunities, partnerships, and funding.' },
            { title: 'Marketing & Branding Manager', desc: 'Develops and implements marketing strategies.' },
            { title: 'Customer Service Manager', desc: 'Ensures excellent patient support and communication.' },
            { title: 'IT & Website Manager', desc: 'Manages telemedicine platform, website, and data security.' },
        ],
    },
    {
        name: 'Logistics',
        color: 'green' as const,
        icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="13" rx="2" /><path d="M16 3v4M8 3v4" /></svg>
        ),
        roles: [
            { title: 'Supply Chain Manager', desc: 'Oversees procurement and distribution of medical equipment & drugs.' },
            { title: 'Warehouse & Inventory Officer', desc: 'Manages stock levels of pharmaceuticals and consumables.' },
            { title: 'Logistics and Delivery Coordinator', desc: 'Ensures efficient transportation of medicines, lab samples, and supplies.' },
        ],
    },
    {
        name: 'Finance',
        color: 'blue' as const,
        icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        roles: [
            { title: 'HR Manager', desc: 'Manages recruitment, training, and staff welfare.' },
            { title: 'Finance & Accounting Officer', desc: 'Handles billing, payroll, and insurance claims.' },
            { title: 'Compliance & Regulatory Officer', desc: 'Ensures adherence to government healthcare regulations.' },
            { title: 'Legal Advisor', desc: 'Manages contracts, business compliance, and legal matters.' },
        ],
    },
    {
        name: 'Support',
        color: 'green' as const,
        icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
        roles: [
            { title: 'Doctors, Nurses, and Healthcare Workers', desc: 'The core patient care team.' },
            { title: 'Customer Care Representatives', desc: 'Handle patient inquiries and complaints.' },
            { title: 'Technicians and IT Support', desc: 'Ensures smooth operation of telemedicine and IT systems.' },
            { title: 'Drivers & Dispatch Riders', desc: 'For mobile healthcare services and deliveries.' },
            { title: 'Security & Maintenance Staff', desc: 'Ensures safety and smooth hospital operations.' },
        ],
    },
];

const divisionStyles = {
    blue: {
        tab: 'bg-lochmara-blue text-white border-lochmara-blue shadow-lg',
        border: 'border-lochmara-blue',
        badge: 'bg-lochmara-blue/10 text-lochmara-blue',
        title: 'text-lochmara-blue',
    },
    green: {
        tab: 'bg-malachite-green text-white border-malachite-green shadow-lg',
        border: 'border-malachite-green',
        badge: 'bg-malachite-green/10 text-malachite-green',
        title: 'text-malachite-green',
    },
};

const TeamMemberCard = ({
    member,
    index,
    featured = false,
}: {
    member: TeamMember;
    index: number;
    featured?: boolean;
}) => (
    <article
        className={`group animate-fade-in overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
            featured ? 'md:col-span-1' : ''
        }`}
        style={{ animationDelay: `${0.1 + index * 0.08}s` }}
    >
        <div className={`relative overflow-hidden ${featured ? 'aspect-[4/5]' : 'aspect-[4/5] sm:aspect-[3/4]'}`}>
            <img
                src={member.img}
                alt={member.name}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-congress-blue/90 via-congress-blue/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-90" />
            <div className="absolute inset-x-0 bottom-0 p-5 text-left text-white">
                <h3 className={`font-ubuntu font-bold leading-tight ${featured ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
                    {member.name}
                </h3>
                <p className={`mt-1 font-redhat font-medium text-white/90 ${featured ? 'text-base md:text-lg' : 'text-sm md:text-base'}`}>
                    {member.role}
                </p>
                {member.hospitalLink && (
                    <Link
                        to="/great-emmanuel-hospital"
                        className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/25 md:text-sm"
                    >
                        The Great Emmanuel Hospital
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </Link>
                )}
            </div>
        </div>
    </article>
);

const OrgTabs = () => {
    const [active, setActive] = useState(0);
    const styles = divisionStyles[divisions[active].color];

    return (
        <>
            <div className="mb-10 flex flex-wrap justify-center gap-3">
                {divisions.map((div, idx) => (
                    <button
                        key={div.name}
                        type="button"
                        onClick={() => setActive(idx)}
                        className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition hover:scale-105 md:text-base ${
                            active === idx ? divisionStyles[div.color].tab : 'border-gray-200 bg-white text-gray-700'
                        }`}
                    >
                        {div.icon}
                        {div.name}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {divisions[active].roles.map((role) => (
                    <div
                        key={role.title}
                        className={`rounded-2xl border-t-4 bg-white p-6 shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl ${styles.border}`}
                    >
                        <span className={`mb-4 inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${styles.badge}`}>
                            {divisions[active].name}
                        </span>
                        <h3 className={`mb-2 font-ubuntu text-lg font-bold ${styles.title}`}>{role.title}</h3>
                        <p className="font-redhat text-sm leading-relaxed text-gray-600">{role.desc}</p>
                    </div>
                ))}
            </div>
        </>
    );
};

const Team = () => {
    const leadership = teamMembers.filter((member) => member.featured);
    const team = teamMembers.filter((member) => !member.featured);

    return (
        <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
            {/* Hero */}
            <section className="relative z-0 isolate flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-brand-light to-blue-100 py-16 md:py-24">
                <div className="relative z-10 mx-auto max-w-3xl px-4 text-center">
                    <span className="animate-fade-in mb-4 inline-flex items-center gap-2 rounded-full border border-lochmara-blue/20 bg-white/80 px-4 py-1.5 font-redhat text-sm font-medium text-congress-blue shadow-sm backdrop-blur-sm">
                        <span className="h-2 w-2 rounded-full bg-malachite-green" />
                        {teamMembers.length} people · one mission
                    </span>
                    <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-sm md:text-5xl">
                        Meet the Minds Behind PureMedify
                    </h1>
                    <p className="animate-fade-in font-redhat text-lg text-gray-700 delay-200 md:text-xl">
                        A passionate, diverse team driving innovation in healthcare, wellness, and community impact across Nigeria.
                    </p>
                </div>
                <svg className="animate-float-slow pointer-events-none absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
                <svg className="animate-float-medium pointer-events-none absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
            </section>

            {/* Leadership */}
            <section className="w-full px-4 py-14 md:py-16">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-10 text-center">
                        <h2 className="font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">Leadership</h2>
                        <p className="mx-auto mt-3 max-w-2xl font-redhat text-gray-600">
                            The founders and executives guiding PureMedify&apos;s vision from the front lines of care.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
                        {leadership.map((member, idx) => (
                            <TeamMemberCard key={member.name} member={member} index={idx} featured />
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Grid */}
            <section className="w-full bg-white/50 px-4 py-14 md:py-16">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-10 text-center">
                        <h2 className="font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">Our Team</h2>
                        <p className="mx-auto mt-3 max-w-2xl font-redhat text-gray-600">
                            The specialists, operators, and care professionals who bring our mission to life every day.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {team.map((member, idx) => (
                            <TeamMemberCard key={member.name} member={member} index={idx} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Organizational Structure */}
            <section className="w-full px-4 py-16 md:py-20">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-10 text-center">
                        <h2 className="font-ubuntu text-3xl font-bold text-congress-blue md:text-4xl">Organizational Structure</h2>
                        <p className="mx-auto mt-3 max-w-2xl font-redhat text-gray-600">
                            Explore the roles and divisions powering PureMedify&apos;s healthcare ecosystem.
                        </p>
                    </div>
                    <OrgTabs />
                </div>
            </section>

            {/* CTA */}
            <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-14">
                <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Want to Join Our Team?</h2>
                <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">
                    We&apos;re always looking for passionate, talented people to help us make a difference. Explore our open roles or reach out to connect.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/careers" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">
                        View Careers
                    </Link>
                    <Link to="/contact" className="rounded-full border-2 border-lochmara-blue bg-white px-8 py-4 font-ubuntu text-lg font-semibold text-lochmara-blue shadow-md transition-transform duration-200 hover:scale-105 hover:border-malachite-green hover:text-malachite-green">
                        Contact Us
                    </Link>
                </div>
            </section>

            <style>{`
                @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
                .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
                .delay-200 { animation-delay: 0.2s; }
                @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
                .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
                @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
                .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
            `}</style>
        </div>
    );
};

export default Team;
